import { call, put, select } from 'redux-saga/effects';

import { api } from './api';
import { environment } from 'environments/environment';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import { AppAction, createReducer, RequestActionTypes } from 'helpers/redux/reduxHelpers';
import { AppState } from 'helpers/store/models/AppState';
import { ACLResourceAccess, FilterUserPrivilegeResourceListPayload } from 'models/ACL';
import { ApiClientRequest } from 'models/ApiClientRequest';
import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';
import {
  ACLActionTypes,
  filterUserPrivilegeResourceListActions,
  getACLKey,
  initialState,
  selectACLContextCpRefNo,
  selectACLState,
  selectUserIdentifiersForUserAccessStoraging,
} from 'saga/acl/ducks';

export const userPrivilegeResources = createReducer(initialState.userPrivilegeResources, {
  [ACLActionTypes.FilterUserPrivilegeResourceList]: {
    [RequestActionTypes.SUCCESS]: (
      state: Record<string, ACLResourceAccess>,
      payload: { requested: string[]; found: string[]; key: string }
    ) => ({
      ...state,
      [payload.key]: {
        ...(state[payload.key] || {}),
        ...payload.requested.reduce(
          (acc: ACLResourceAccess, item: string) => ({
            ...acc,
            [item]: {
              isGranted: payload.found.includes(item),
              recordTime: new Date(),
            },
          }),
          {}
        ),
      },
    }),
  },
});

/* SELECTORS */
const selectACLUserPrivilegeResources = (state: AppState): Record<string, ACLResourceAccess> =>
  selectACLState(state).userPrivilegeResources;

const selectGetUserPrivilegeResources = (
  state: AppState
): ((payload: FilterUserPrivilegeResourceListPayload) => ACLResourceAccess | undefined) => {
  const keyPart = selectUserIdentifiersForUserAccessStoraging(state);
  const contextCpRefNo = selectACLContextCpRefNo(state);

  return ({ entityRefNo, privilegeType, resourceType }: FilterUserPrivilegeResourceListPayload) =>
    selectACLUserPrivilegeResources(state)[
      getACLKey(keyPart, contextCpRefNo ? entityRefNo : '', privilegeType, resourceType)
    ];
};

export const selectNotExistingUserPrivilegeResources = (
  state: AppState
): ((payload: FilterUserPrivilegeResourceListPayload) => string[]) => {
  return (payload: FilterUserPrivilegeResourceListPayload) => {
    const existing = selectGetUserPrivilegeResources(state)(payload);

    return payload.resourcesList.filter((r) => !(existing && r in existing));
  };
};

export const selectFilterUserPrivilegeResourceList = (
  state: AppState
): ((payload: FilterUserPrivilegeResourceListPayload) => string[]) => {
  return (payload: FilterUserPrivilegeResourceListPayload) => {
    const existing = selectGetUserPrivilegeResources(state)(payload);

    return payload.resourcesList.filter((r) => existing && r in existing && existing[r].isGranted);
  };
};

/* SAGAS */
export function* filterUserPrivilegeResourceList({ payload }: AppAction<FilterUserPrivilegeResourceListPayload>) {
  const contextCpRefNo: string | undefined = yield select(selectACLContextCpRefNo);
  const entityRefNo = contextCpRefNo ? payload.entityRefNo : undefined;
  const getMissingPrivileges: (payload: FilterUserPrivilegeResourceListPayload) => string[] = yield select(
    selectNotExistingUserPrivilegeResources
  );
  const missingPrivileges = getMissingPrivileges(payload);

  if (!missingPrivileges.length) {
    return;
  }

  const request: ApiClientRequest = {
    apiCall: call(
      api.filterAclUserPrivilegeResourceList,
      yield getApiUrl(environment.urlConfig.userAccess),
      payload.privilegeType,
      payload.resourceType,
      missingPrivileges,
      payload.cpRefNo,
      entityRefNo
    ),
    skipUserActivityLog: true,
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    const keyPart: string = yield select(selectUserIdentifiersForUserAccessStoraging);

    yield put(
      filterUserPrivilegeResourceListActions.success({
        requested: missingPrivileges,
        found: resp.data,
        key: getACLKey(keyPart, entityRefNo, payload.privilegeType, payload.resourceType),
      })
    );
  } else {
    yield put(filterUserPrivilegeResourceListActions.failure());
  }
}
